import { useCallback, useEffect, useRef, useState } from 'react';
import { isAbortError } from './salesUtils';

export const DEBOUNCE_MS = 300;

// Fetches fetcher(params, {signal}) whenever params change.
// - First load and Retry fire immediately; later param changes are debounced 300ms.
// - Each change aborts the in-flight request (AbortController); abort errors are ignored.
// - The last successful data stays visible while refetching (loading=true, data kept).
export default function useSalesQuery(fetcher, params) {
  const key = JSON.stringify(params);
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const [attempt, setAttempt] = useState(0);
  const immediate = useRef(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const controller = new AbortController();
    const run = () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      fetcherRef.current(JSON.parse(key), { signal: controller.signal })
        .then((res) => {
          if (!controller.signal.aborted) setState({ data: res.data, error: null, loading: false });
        })
        .catch((err) => {
          if (controller.signal.aborted || isAbortError(err)) return;
          setState((s) => ({ ...s, error: err, loading: false }));
        });
    };
    let timer = null;
    if (immediate.current) {
      immediate.current = false;
      run();
    } else {
      setState((s) => ({ ...s, loading: true }));
      timer = setTimeout(run, DEBOUNCE_MS);
    }
    return () => {
      if (timer) clearTimeout(timer);
      controller.abort();
    };
  }, [key, attempt]);

  const retry = useCallback(() => {
    immediate.current = true;
    setAttempt((n) => n + 1);
  }, []);

  return { ...state, retry };
}
